import React from 'react'
import { SlideHeaderChrome } from './SlideHeaderChrome'
import { SlideHeadline } from '../primitives/SlideHeadline'
import { TextClamp } from '../components/text/TextClamp'
import { EditableText } from '../slides/EditableText'
import '../foundations/tokens.css'
import '../foundations/slide-styles.css'

export interface SlideFrameProps {
  children: React.ReactNode
  eyebrowText?: string
  clientLogoUrl?: string
  footerText?: string
  actionTitle?: string
  actionSubtitle?: string
  /** Default `uppercase` (most slides). Use `none` for sentence-case eyebrows (e.g. Key findings). */
  eyebrowTextTransform?: 'uppercase' | 'none'
  fullBleed?: boolean
  background?: string
  className?: string
  /**
   * `fill` (default) — main column grows so children can use flex height.
   * `start` — align children to the top (e.g. card grids: avoid extra empty space below).
   */
  contentVerticalAlign?: 'fill' | 'start'
  /** The headline's line budgets, from the host layout's bound slots (see SlideHeadline). */
  headlineMaxLines?: { title: number; subtitle?: number }
  editable?: boolean
  onEdit?: (field: string, value: string) => void
}

export function SlideFrame({
  children,
  eyebrowText,
  clientLogoUrl,
  footerText,
  actionTitle,
  actionSubtitle,
  eyebrowTextTransform = 'uppercase',
  fullBleed = false,
  background,
  className,
  contentVerticalAlign = 'fill',
  headlineMaxLines,
  editable,
  onEdit,
}: SlideFrameProps) {
  const resolvedLogoUrl = clientLogoUrl ?? null
  const normalizedFooterText = (footerText || '').replace(/\s+/g, ' ').trim()
  const hasFooter = Boolean(normalizedFooterText)

  if (fullBleed) {
    return (
      <div
        className={`ds-slide ${className ?? ''}`}
        style={{
          width: 'var(--ds-slide-width)',
          height: 'var(--ds-slide-height)',
          maxHeight: 'var(--ds-slide-height)',
          background: background || 'var(--ds-slide-bg)',
          fontFamily: 'var(--ds-font-sans)',
          color: 'var(--ds-text-primary)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {children}
        {hasFooter && (
          <footer
            style={{
              padding: `var(--ds-footer-pad-y) var(--ds-slide-pad-x)`,
              fontSize: 'var(--ds-fs-footer)',
              color: 'var(--ds-text-faint)',
              textAlign: 'right' as const,
              borderTop: `var(--ds-border-thin) solid var(--ds-border-color)`,
            }}
          >
            {editable && onEdit ? (
              <EditableText
                value={normalizedFooterText}
                field="footerText"
                editable
                onEdit={onEdit}
                tag="span"
              />
            ) : (
              <TextClamp minLines={1} maxLines={1} lineHeight="var(--ds-lh-snug)">
                {normalizedFooterText}
              </TextClamp>
            )}
          </footer>
        )}
      </div>
    )
  }

  return (
    <div
      className={`ds-slide ${className ?? ''}`}
      style={{
        width: 'var(--ds-slide-width)',
        height: 'var(--ds-slide-height)',
        maxHeight: 'var(--ds-slide-height)',
        background: background || 'var(--ds-slide-bg)',
        fontFamily: 'var(--ds-font-sans)',
        color: 'var(--ds-text-primary)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <SlideHeaderChrome
        eyebrowText={eyebrowText}
        clientLogoUrl={resolvedLogoUrl}
        eyebrowTextTransform={eyebrowTextTransform}
      />

      <div
        style={{
          padding: `var(--ds-slide-content-pad-y) var(--ds-slide-pad-x) ${hasFooter ? '0' : 'var(--ds-slide-pad-x)'}`,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <SlideHeadline actionTitle={actionTitle} actionSubtitle={actionSubtitle} maxLines={headlineMaxLines} editable={editable} onEdit={onEdit} />

        <div
          style={{
            flex: contentVerticalAlign === 'start' ? '0 0 auto' : 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: contentVerticalAlign === 'start' ? 'flex-start' : undefined,
            width: '100%',
          }}
        >
          {children}
        </div>
      </div>

      {hasFooter && (
        <footer
          style={{
            padding: `var(--ds-footer-pad-y) var(--ds-slide-pad-x)`,
            fontSize: 'var(--ds-fs-footer)',
            color: 'var(--ds-text-faint)',
            textAlign: 'right' as const,
            borderTop: `var(--ds-border-thin) solid var(--ds-border-color)`,
          }}
        >
          {editable && onEdit ? (
            <EditableText
              value={normalizedFooterText}
              field="footerText"
              editable
              onEdit={onEdit}
              tag="span"
            />
          ) : (
            <TextClamp minLines={1} maxLines={1} lineHeight="var(--ds-lh-snug)">
              {normalizedFooterText}
            </TextClamp>
          )}
        </footer>
      )}
    </div>
  )
}
